import React, { useCallback, useRef } from 'react';
import './AlertManagement.scss';
import { InternalApiScreeningAlertStatus } from '../../api-internal';
import { MenuItem, SalvMenuButton, Size, SubmenuButton } from '@salvapp/ui-components';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { useQuickStatusNotes } from '../../screening/alert/status/useQuickStatusNotes';
import useShortcut from '../../common/useShortcut';

interface Props {
  label: string;
  icon?: IconDefinition;
  size: Size;
  showNotes: boolean;
  selectableStatuses: InternalApiScreeningAlertStatus[];
  shortcutTriggerKey?: string;

  selectStatus: (status: string) => void;
  selectNote: (status: string, note: string) => void;
}

export const AlertStatusSelection = ({
  label,
  icon,
  size,
  showNotes,
  selectableStatuses,
  selectStatus,
  selectNote,
  shortcutTriggerKey,
}: Props) => {
  const [quickStatusNotes, quickNotesByStatusCode] = useQuickStatusNotes();

  const ref = useRef<HTMLButtonElement>(null);
  const openMenu = useCallback(() => {
    ref.current?.click();
  }, [ref]);
  useShortcut({ triggerKey: shortcutTriggerKey, onTrigger: openMenu });

  const selectStatusOrNote = useCallback(
    (selector: (string | undefined)[]) => {
      const statusCode = selector[0];
      const quickNote = quickStatusNotes.find((qsn) => qsn.id?.toString() === selector[1]);
      if (statusCode && quickNote) {
        selectNote(statusCode, quickNote.comment);
      } else if (statusCode) {
        selectStatus(statusCode);
      }
    },
    [quickStatusNotes, selectStatus, selectNote]
  );

  return (
    <SalvMenuButton
      size={size}
      onAction={selectStatusOrNote}
      label={label}
      icon={icon}
      enableFilter
      ref={ref}
      filterPlaceholder={'Search...'}
      className={'status-assignment-menu-button'}
    >
      {selectableStatuses.map((status) => {
        const qsn = quickNotesByStatusCode.get(status.code);

        return (
          <MenuItem key={status.code} title={status.textName} value={status.code}>
            {qsn && showNotes ? (
              <SubmenuButton
                details={`${qsn.length} status notes`}
                onAction={([noteId]) => selectStatusOrNote([status.code, noteId])}
              >
                {qsn.map((quickNote) => (
                  <MenuItem
                    key={quickNote.comment}
                    title={quickNote.comment}
                    value={quickNote.id!.toString()}
                    clipWidth
                  />
                ))}
              </SubmenuButton>
            ) : undefined}
          </MenuItem>
        );
      })}
    </SalvMenuButton>
  );
};
